import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from "react-router-dom";
import { useGlobal } from 'reactn';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import { Collapse, Button } from 'react-bootstrap';

import useLocalStorage from "stores/useLocalStorage.js";
import { fmtPrice, fmtPercent } from 'stores/utils';
import { useAuth } from 'stores/auth';
import { useApi } from 'stores/api';
import { imgUrl } from 'stores/utils';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import FixedBottom from 'components/FixedBottom';

const BWOptions = (props) => {
  let { action } = useParams();
  let history = useHistory();
  const { t } = useTranslation(['wizard-options']);
  const auth = useAuth();
  const api = useApi();
  const [state, setState] = useState({
    notes1: '',
    notes2: '',
  });
  const [currentTxn, setCurrentTxn] = useState(null);
  const [txnSvcsMap, setTxnSvcsMap] = useState({});
  const [txnAccsMap, setTxnAccsMap] = useState({});
  const [accQtyMap, setAccQtyMap] = useState({});
  const [objectDetails, setObjectDetails] = useState(null);

  const [LSObjectDetailsMap] = useLocalStorage('ls_odm');
  const [masterData] = useGlobal('masterData');
  const [modalProps, setModalProps] = useGlobal('modalProps');
  const [showLoader, setShowLoader] = useGlobal('showLoader');
  const [searchParams] = useGlobal('searchParams');
  const [orderId] = useGlobal('orderId');
  const [currentTxnId] = useGlobal('currentTxnId');
  const [currentObjId] = useGlobal('currentObjId');
  const [cartCount] = useGlobal('cartCount');

  const [accOpen, setAccOpen] = useState({});

  const toggleAccOpen = (id) => {
    setAccOpen({ ...accOpen, [id]: !accOpen[id] });
  }

  const onClickOverview = async (e) => {
    e.preventDefault();
    setShowLoader(true);
    let order;
    if (!cartCount) {
      order = await api.createOrUpdateOrder(null, currentTxnId);
      // if (auth.user) {
      //   await api.setLesseeData(order);
      // }
    } else {
      if (action === 'edit') {
        order = await api.getOrderDetails(orderId);
      } else if (action === 'add') {
        order = await api.createOrUpdateOrder(orderId, currentTxnId);
      }
    }
    setShowLoader(false);
    if (order) {
      history.push('/wizard/overview');
    }
  }

  const onClickCancel = async (e) => {
    e.preventDefault();
    setShowLoader(true);
    let result = true;
    if (currentTxnId) {
      result = api.cancelTemporaryBooking();
    }
    setShowLoader(false);
    if (result) {
      history.push(`/search`);
    }
  }

  useEffect(() => {
    async function onInit() {
      setObjectDetails(LSObjectDetailsMap[currentObjId]);
      let cTxn = await api.fetchTransaction(currentTxnId);
      setCurrentTxn(cTxn);
      setShowLoader(false);
    }
    setShowLoader(true);
    onInit();
  }, []);

  useEffect(() => {
    if (currentTxn) {
      let svcMap = {};
      currentTxn.RentalTransactionService.forEach(svcItem => {
        svcMap[svcItem.refRentalServiceId] = { ...svcItem };
      })
      setTxnSvcsMap(svcMap);

      let accMap = {};
      currentTxn.RentalTransactionAccessory.forEach(accItem => {
        accMap[accItem.refRentalObjectId] = { ...accItem };
      })
      setTxnAccsMap(accMap);
      setAccQtyMap({});
    }
  }, [currentTxn]);

  const rentalObjectOptions = () => {
    const services = objectDetails.objectOptions.Services || [];
    const objAccessories = objectDetails.objectOptions.Accessories || [];
    const accGrouped = Object.groupBy(objAccessories, ({ AccessoryRentalObject_RentalSubcategory_Name }) => AccessoryRentalObject_RentalSubcategory_Name);
    const currency = objectDetails.businessUnit.Currency_Name;

    const getServicePrice = (svc) => {
      return !svc.PercentagePrice ? `${currency} ${fmtPrice(svc.FixedPrice)}` : `${fmtPercent(svc.PercentagePrice)}%`;
    }

    const onChangeService = async (isChecked, e, id) => {
      setShowLoader(true);
      if (isChecked) {
        console.log('adding service...');
        const txn = await api.addService(currentTxn.Id, id);
        setCurrentTxn(txn);
      } else {
        console.log('removing service...');
        const txn = await api.removeService(currentTxn.Id, id);
        setCurrentTxn(txn);
      }
      setShowLoader(false);
    };

    const getAccOfferPrice = (offer) => {
      let offerPrice = offer.OfferPrice || offer.FlatPrice;
      return `${currency} ${fmtPrice(offerPrice)}`;
    }

    let canInc = true;
    let canDec = true;

    const onIncreaseQuantity = async (accessory) => {
      setShowLoader(true);
      let accId = accessory.refRentalAccessoryId;
      let maxQuantity = accessory.AccessoryRentalObject_Quantity;
      let minQuantity = accessory.AccessoryRentalObject_MinRentalQuantity || 1;
      let quantity = txnAccsMap[accId] ? txnAccsMap[accId]['Quantity'] : minQuantity;
      if (txnAccsMap[accId]) {
        quantity += 1;
      }
      if (quantity > maxQuantity) {
        setShowLoader(false);
        return;
      }
      let txn;
      if (quantity === minQuantity) {
        txn = await api.setAccessory(currentTxn.Id, accId, quantity);
      } else {
        txn = await api.updateAccessory(currentTxn.Id, accId, quantity);
      }
      setShowLoader(false);
      if (txn) {
        setCurrentTxn(txn);
      }
    }

    const onDecreaseQuantity = async (accessory) => {
      const accId = accessory.refRentalAccessoryId;
      const minQuantity = accessory.AccessoryRentalObject_MinRentalQuantity;
      setShowLoader(true);
      let quantity = txnAccsMap[accId] ? txnAccsMap[accId]['Quantity'] : 0;
      if ((accessory.IsObligatory && quantity === minQuantity) || (quantity <= 0)) {
        setShowLoader(false);
        return;
      }
      let newQuantity = (!accessory.IsObligatory && quantity === minQuantity) ? 0 : quantity - 1;
      let txn = await api.updateAccessory(currentTxn.Id, accId, newQuantity);
      setShowLoader(false);
      if (txn) {
        setCurrentTxn(txn);
      }
    }

    const isSvcInTxn = (id) => {
      //return currentTxn.RentalTransactionService.find(svc => svc.refRentalServiceId === id) ? true : false;
      return txnSvcsMap[id] ? true : false;
    }

    const getServiceSumPrice = (service) => {
      const svcId = service.refRentalServiceId;
      return txnSvcsMap[svcId] ? currency + ' ' + (txnSvcsMap[svcId]['ResultPrice']).toFixed(2) : '';
    }

    const getAccessoryQuantity = (accessory) => {
      const accId = accessory.refRentalAccessoryId;
      return accQtyMap[accId] ? accQtyMap[accId] : (txnAccsMap[accId] ? txnAccsMap[accId]['Quantity'] : 0);
    }

    const setAccessoryQuantityTemp = (e, accessory) => {
      const accId = accessory.refRentalAccessoryId;
      let quantity = e.target.value.replace(/\D/, '');
      setAccQtyMap({ ...accQtyMap, [accId]: quantity });
    }

    const setAccessoryQuantity = async (accessory) => {
      setShowLoader(true);
      const accId = accessory.refRentalAccessoryId;
      const maxQuantity = accessory.AccessoryRentalObject_Quantity;
      const minQuantity = accessory.AccessoryRentalObject_MinRentalQuantity;
      let newQuantity = accQtyMap[accId];
      if (!newQuantity) {
        setShowLoader(false);
        return;
      }
      newQuantity = parseInt(newQuantity, 10);
      if (newQuantity > maxQuantity) {
        setShowLoader(false);
        setModalProps({
          ...modalProps,
          showModal: true,
          messages: [t("maxAllowedQuantity") + maxQuantity]
        });
      } else if ((accessory.IsObligatory || newQuantity > 0) && (newQuantity < minQuantity)) {
        setShowLoader(false);
        setModalProps({
          ...modalProps,
          showModal: true,
          messages: [t("minAllowedQuantity") + minQuantity]
        });
      } else {
        let txn;
        if (txnAccsMap[accId]) {
          txn = await api.updateAccessory(currentTxn.Id, accId, newQuantity);
        } else {
          txn = await api.setAccessory(currentTxn.Id, accId, newQuantity);
        }
        setShowLoader(false);
        if (txn) {
          setCurrentTxn(txn);
        }
      }
    }

    const handleKeyDown = (e, accessory) => {
      if (e.key === 'Enter') {
        console.log('enter press... validate and update');
        setAccessoryQuantity(accessory);
      }
    }

    const handleBlur = (e, accessory) => {
      console.log('blur event... validate and update');
      setAccessoryQuantity(accessory);
    }

    const getAccessorySumPrice = (accessory) => {
      const accId = accessory.refRentalAccessoryId;
      return txnAccsMap[accId] ? currency + ' ' + (txnAccsMap[accId]['SumTotalPrice']).toFixed(2) : '';
    }

    return (
      <>
        {(services.length === 0 && objAccessories.length === 0) &&
          <div className="services-entry">
            <p>{t("noOptions")}</p>
          </div>
        }
        {services.length > 0 && <div className="services-entry">
          <h5>{t("services")}</h5>
          {services.map((service, idx) =>
            <div className="list-entry">
              <div className="input">{service.Name}</div>
              {(!masterData.organization.FreeOfCharge) && <div className="value">{getServicePrice(service)}</div>}
              <div className="toggler">
                <Switch
                  checked={isSvcInTxn(service.refRentalServiceId)}
                  onChange={onChangeService}
                  onColor="#B2B2B2"
                  onHandleColor="#87A0FF"
                  offColor="#B2B2B2"
                  offHandleColor="#FFFFFF"
                  handleDiameter={20}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  width={50}
                  height={20}
                  boxShadow="0 0.25em 0.25em 0 rgba(52, 52, 54, 0.25)"
                  activeBoxShadow="0 0.25em 0.25em 0 rgba(52, 52, 54, 0.25)"
                  className={isSvcInTxn(service.refRentalServiceId) ? "react-switch on" : "react-switch off"}
                  id={service.refRentalServiceId}
                />
              </div>
              {(!masterData.organization.FreeOfCharge) && <div className="price">
                {getServiceSumPrice(service)}
              </div>}
            </div>
          )}
        </div>}
        {
          Object.entries(accGrouped).map(([groupName, accessories]) => {
            return (
              <div className="services-entry">
                {<h5 key={groupName}>{groupName}</h5>}
                {accessories.map((accessory, idx1) =>
                  <>
                    {
                      accessory.AccessoryRentalObjectOffer.map((offer, idx2) =>
                        <>
                          <div className="list-entry" key={idx2}>
                            <div className="input">
                              {accessory.AccessoryRentalObject_Name} - {offer.DisplayName}&nbsp;
                              {accessory.AccessoryRentalObjectAttachment_FirstId &&
                                <a className="accDetails-trigger" onClick={() => toggleAccOpen(accessory.Id)}>
                                  {t("more")} <i class="fa fa-caret-down" aria-hidden="true"></i>
                                </a>}
                            </div>
                            {(!masterData.organization.FreeOfCharge) && <div className="value">{getAccOfferPrice(offer)}</div>}
                            <div className="amount">
                              <input placeholder="000" value={getAccessoryQuantity(accessory)}
                                onChange={e => setAccessoryQuantityTemp(e, accessory)}
                                onBlur={e => handleBlur(e, accessory)}
                                onKeyDown={e => handleKeyDown(e, accessory)} />
                              <a className={"minus" + (canDec ? "" : " unselectable")} onClick={() => onDecreaseQuantity(accessory)}><i className="fa fa-minus"></i></a>
                              <a className={"plus" + (canInc ? "" : " unselectable")} onClick={() => onIncreaseQuantity(accessory)}><i className="fa fa-plus"></i></a>
                            </div>
                            {(!masterData.organization.FreeOfCharge) && <div className="price">
                              {getAccessorySumPrice(accessory)}
                            </div>}
                          </div>
                          <Collapse in={accOpen[accessory.Id]}>
                            <div className="acc-details">
                              <div className="accDetails-img">
                                <img src={imgUrl(accessory.AccessoryRentalObjectAttachment_FirstId)} />
                              </div>
                              <div className="accDetails-description" dangerouslySetInnerHTML={{ __html: accessory.AccessoryRentalObject_Description }}></div>
                            </div>
                          </Collapse>
                        </>
                      )
                    }
                  </>
                )}
              </div>
            )
          })
        }
      </>
    );
  }

  if (!currentTxn || !objectDetails) return (<div><Loader /></div>);

  return (
    <>
      {showLoader && <Loader />}
      <Stepper activeStep="options" />
      <section className="wizard-container booking-view">
        <div className="wrapper">
          <div className="row wizard-content">
            <div className="column-12">
              <h3 className="mali-blue">{t("options")}</h3>
              <h4 className="mali-blue">{objectDetails && objectDetails.rentalObject.Name}</h4>
              {objectDetails.objectOptions && rentalObjectOptions()}
            </div>
          </div>
        </div>
      </section>

      {
        <FixedBottom>
          <div className="wrapper">
            <div className="row fixedBottom-content">
              <div className="column-8 fixedBottom-right">
                {(!masterData.organization.FreeOfCharge) && <div className="fixedBottom-price">
                  <span className="amount">{objectDetails.businessUnit.Currency_Name} {fmtPrice(currentTxn.TotalDiscountedPrice)}</span>
                </div>}
                <div className="fixedBottom-buttons btn-forward">
                  {
                    !cartCount &&
                    <>
                      {
                        !auth.user && <Link to={`/wizard/personaldata`} className="book-object">
                          {t("btnPersonalData")} <i className="fa fa-arrow-right" aria-hidden="true"></i>
                        </Link>
                      }
                      {
                        auth.user && <Link to={`/wizard/overview`} onClick={onClickOverview} className="book-object">
                          {t("btnOverview")}<i className="fa fa-arrow-right" aria-hidden="true"></i>
                        </Link>
                      }
                    </>
                  }
                  {
                    cartCount > 0 && <Link to={`/wizard/overview`} onClick={onClickOverview} className="book-object">
                      {t("btnOverview")} <i className="fa fa-arrow-right" aria-hidden="true"></i>
                    </Link>
                  }
                </div>
              </div>

              <div className="column-4 fixedBottom-left">
                <div className="fixedBottom-buttons btn-back">
                  <Link to={`/search`} onClick={onClickCancel} className="book-object">
                    <i className="fa fa-arrow-left" aria-hidden="true"></i> {t("btnCancel")}
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </FixedBottom>
      }
    </>
  );
}

export default BWOptions;